import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import {
  Bell, Plus, Eye, Pencil, Archive, Trash2, CheckCircle2, Loader2,
  Calendar, Users, BarChart3, FileText, Send,
} from 'lucide-react';
import type {
  ApiError, NoticeWithMeta, NoticeType, NoticePriority, NoticeStatus,
  CreateNoticeInput,
} from '@pms/shared';
import { createNoticeSchema, PERMISSIONS } from '@pms/shared';
import { noticeService } from '@/services/notice.service';
import { departmentsService } from '@/services/departments.service';
import { usersService, type StaffListItem } from '@/services/users.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { DataTable, type Column } from '@/components/ui/data-table';
import { StatCard } from '@/components/ui/stat-card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs } from '@/components/ui/tabs';
import { SearchInput } from '@/components/ui/search-input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { formatDate, formatDateTime } from '@/lib/dateFormat';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const ALL = '__all__';

const PRIORITY_STYLES: Record<NoticePriority, string> = {
  critical: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-900/50',
  high:     'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900/50',
  normal:   'bg-teal-100 text-teal-700 border-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-900/50',
  low:      'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
};

const TYPE_STYLES: Record<NoticeType, string> = {
  urgent:   'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-900/40',
  policy:   'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-900/20 dark:text-violet-400 dark:border-violet-900/40',
  event:    'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/40',
  circular: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/40',
  general:  'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-400 dark:border-teal-900/40',
};

const STATUS_STYLES: Record<NoticeStatus, string> = {
  draft:     'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-900/50',
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-900/50',
  archived:  'bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
};

const STATUS_LABELS: Record<NoticeStatus, string> = {
  draft: 'Draft', published: 'Published', archived: 'Archived',
};

type DialogState =
  | { type: 'form'; notice: NoticeWithMeta | null }
  | { type: 'view'; notice: NoticeWithMeta }
  | { type: 'delete'; notice: NoticeWithMeta }
  | null;

// ── Employee multi-select dropdown ────────────────────────────────────────────

function EmployeeMultiSelect({
  allUsers,
  value,
  onChange,
}: {
  allUsers: StaffListItem[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = search.trim()
    ? allUsers.filter((u) =>
        `${u.first_name} ${u.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
        u.employee_code?.toLowerCase().includes(search.toLowerCase())
      )
    : allUsers;

  const selectedUsers = allUsers.filter((u) => value.includes(u.id));

  const toggle = (id: string) => {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => e.key === 'Enter' && setOpen((o) => !o)}
        className="min-h-9 w-full cursor-pointer rounded-md border border-input bg-background px-3 py-1.5 text-xs flex flex-wrap gap-1 items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {selectedUsers.length === 0 ? (
          <span className="text-muted-foreground">Select employees…</span>
        ) : (
          selectedUsers.map((u) => (
            <span key={u.id} className="inline-flex items-center gap-0.5 rounded bg-primary/10 text-primary px-1.5 py-0.5 text-[11px] font-medium">
              {u.first_name} {u.last_name}
              <button
                type="button"
                onMouseDown={(e) => { e.stopPropagation(); toggle(u.id); }}
                className="ml-0.5 rounded hover:text-rose-500 leading-none"
              >
                ×
              </button>
            </span>
          ))
        )}
        <span className="ml-auto text-muted-foreground text-[10px]">▾</span>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-background shadow-lg">
          <div className="p-2 border-b border-border/40">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or code…"
              className="w-full h-7 rounded border border-input bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div className="max-h-44 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="py-3 text-center text-xs text-muted-foreground">No employees found</p>
            ) : (
              filtered.map((u) => (
                <label key={u.id} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 text-xs hover:bg-muted/50">
                  <Checkbox
                    checked={value.includes(u.id)}
                    onChange={() => toggle(u.id)}
                  />
                  <span className="flex-1 truncate">{u.first_name} {u.last_name}</span>
                  {u.department_name && (
                    <span className="shrink-0 text-[10px] text-muted-foreground">{u.department_name}</span>
                  )}
                </label>
              ))
            )}
          </div>
          {value.length > 0 && (
            <div className="border-t border-border/40 px-2 py-1.5 flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">{value.length} selected</span>
              <button type="button" onClick={() => onChange([])} className="text-[11px] text-rose-500 hover:underline">
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Notice form dialog ────────────────────────────────────────────────────────

function NoticeFormDialog({
  notice,
  onClose,
}: {
  notice: NoticeWithMeta | null;
  onClose: () => void;
}) {
  const isEdit = !!notice;
  const qc = useQueryClient();

  const [publishAfterSave, setPublishAfterSave] = useState(false);


  const deptQuery = useQuery({
    queryKey: ['departments', 'all'],
    queryFn: () => departmentsService.list({ limit: 100 }),
  });

  const usersQuery = useQuery({
    queryKey: ['users', 'all-for-notice'],
    queryFn: () => usersService.list({ limit: 100 }),
  });

  const { register, handleSubmit, control, watch, formState } = useForm<CreateNoticeInput>({
    resolver: zodResolver(createNoticeSchema),
    defaultValues: {
      title:                   notice?.title ?? '',
      content:                 notice?.content ?? '',
      notice_type:             notice?.notice_type ?? 'general',
      priority:                notice?.priority ?? 'normal',
      target_type:             notice?.target_type ?? 'all',
      requires_acknowledgment: notice?.requires_acknowledgment ?? false,
      expires_at:              notice?.expires_at ?? null,
      scheduled_for:           notice?.scheduled_for ?? null,
      recipient_ids:           [],
    },
  });

  const targetType = watch('target_type');
  const { errors, isSubmitting } = formState;

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['notices', 'admin'] });
    void qc.invalidateQueries({ queryKey: ['notices', 'my'] });
  };

  const mutation = useMutation({
    mutationFn: async (values: CreateNoticeInput) => {
      if (isEdit) {
        await noticeService.update(notice!.id, values);
        if (publishAfterSave) await noticeService.publish(notice!.id);
      } else {
        const { id } = await noticeService.create(values);
        if (publishAfterSave) await noticeService.publish(id);
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Notice updated' : publishAfterSave ? 'Notice published' : 'Draft saved');
      invalidate();
      onClose();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Notice' : 'New Notice'}</DialogTitle>
          <DialogDescription>
            {isEdit ? 'Update the notice details below.' : 'Fill in the details to create a notice.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
          {/* Title */}
          <div className="space-y-1.5">
            <Label htmlFor="title" className="text-xs font-medium">Title <span className="text-rose-500">*</span></Label>
            <Input id="title" {...register('title')} placeholder="Notice title" className="h-9 text-sm" />
            {errors.title && <p className="text-xs text-rose-500">{errors.title.message}</p>}
          </div>

          {/* Type + Priority (2-col) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Notice Type <span className="text-rose-500">*</span></Label>
              <Controller
                name="notice_type"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                      <SelectItem value="policy">Policy</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="circular">Circular</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Priority <span className="text-rose-500">*</span></Label>
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Content <span className="text-rose-500">*</span></Label>
            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <RichTextEditor value={field.value} onChange={field.onChange} />
              )}
            />
            {errors.content && <p className="text-xs text-rose-500">{errors.content.message}</p>}
          </div>

          {/* Target type */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Target Audience</Label>
            <Controller
              name="target_type"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Employees</SelectItem>
                    <SelectItem value="specific">Specific (departments &amp; employees)</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          {/* Specific recipients (shown when specific) */}
          {targetType === 'specific' && (
            <div className="space-y-3 rounded-lg border border-border/40 bg-muted/20 p-3">
              {/* Departments */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Departments</Label>
                {deptQuery.isLoading ? (
                  <p className="text-xs text-muted-foreground">Loading…</p>
                ) : (
                  <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    <Controller
                      name="recipient_ids"
                      control={control}
                      render={({ field }) => {
                        const selected = (field.value ?? []).filter((r) => r.recipient_type === 'department').map((r) => r.recipient_id);
                        return (
                          <>
                            {(deptQuery.data?.data ?? []).map((dept) => (
                              <label key={dept.id} className="flex items-center gap-1.5 cursor-pointer text-xs">
                                <Checkbox
                                  checked={selected.includes(dept.id)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    const current = (field.value ?? []).filter((r) => r.recipient_type !== 'department' || r.recipient_id !== dept.id);
                                    if (checked) {
                                      field.onChange([...current, { recipient_type: 'department', recipient_id: dept.id }]);
                                    } else {
                                      field.onChange(current);
                                    }
                                  }}
                                />
                                {dept.name}
                              </label>
                            ))}
                          </>
                        );
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-border/40" />

              {/* Employees */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Specific Employees</Label>
                {usersQuery.isLoading ? (
                  <p className="text-xs text-muted-foreground">Loading…</p>
                ) : (
                  <Controller
                    name="recipient_ids"
                    control={control}
                    render={({ field }) => {
                      const selectedIds = (field.value ?? [])
                        .filter((r) => r.recipient_type === 'user')
                        .map((r) => r.recipient_id);
                      return (
                        <EmployeeMultiSelect
                          allUsers={usersQuery.data?.data ?? []}
                          value={selectedIds}
                          onChange={(ids) => {
                            const withoutUsers = (field.value ?? []).filter((r) => r.recipient_type !== 'user');
                            field.onChange([
                              ...withoutUsers,
                              ...ids.map((id) => ({ recipient_type: 'user' as const, recipient_id: id })),
                            ]);
                          }}
                        />
                      );
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {/* Options row */}
          <div className="rounded-lg border border-border/30 bg-muted/20 p-3">
            <Controller
              name="requires_acknowledgment"
              control={control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer text-xs font-medium">
                  <Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />
                  Requires Acknowledgment
                </label>
              )}
            />
          </div>

          <DialogFooter className="flex gap-2 pt-2">
            <Button type="button" variant="ghost" size="sm" className="h-8 text-xs" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={isSubmitting}
              onClick={() => setPublishAfterSave(false)}
            >
              {isSubmitting && !publishAfterSave && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              Save as Draft
            </Button>
            <Button
              type="submit"
              size="sm"
              className="h-8 text-xs bg-teal-600 hover:bg-teal-700 text-white"
              disabled={isSubmitting}
              onClick={() => setPublishAfterSave(true)}
            >
              {isSubmitting && publishAfterSave && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
              <Send className="mr-1.5 h-3 w-3" />
              Publish Now
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Notice view dialog (admin, with compliance) ───────────────────────────────

function NoticeViewDialog({
  notice,
  onClose,
}: {
  notice: NoticeWithMeta;
  onClose: () => void;
}) {
  const [readsPage, setReadsPage] = useState(1);

  const readsQuery = useQuery({
    queryKey: ['notices', 'reads', notice.id, readsPage],
    queryFn: () => noticeService.listReads(notice.id, { page: readsPage, limit: 10 }),
    enabled: true,
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xs font-mono text-muted-foreground">{notice.notice_number}</span>
            <Badge variant="outline" className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[notice.status]}`}>
              {STATUS_LABELS[notice.status]}
            </Badge>
            <Badge variant="outline" className={`text-[10px] px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[notice.priority]}`}>
              {notice.priority}
            </Badge>
            <Badge variant="outline" className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_STYLES[notice.notice_type]}`}>
              {notice.notice_type}
            </Badge>
          </div>
          <DialogTitle className="text-base font-semibold">{notice.title}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {formatDateTime(notice.published_at)}</span>
              <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {notice.read_count} read</span>
              <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {notice.ack_count} acknowledged</span>
              {notice.target_type === 'specific' && (
                <span className="flex items-center gap-1"><Users className="h-3 w-3" /> Specific audience</span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-border/30 bg-background/60 p-4">
          <RichTextContent html={notice.content} />
        </div>

        {/* Delivery stats */}
        {notice.delivered_count > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 rounded-lg border border-border/30 bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><Send className="h-3 w-3" /> Delivered to <span className="font-medium text-foreground">{notice.delivered_count}</span> users</span>
            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> Read: <span className="font-medium text-foreground">{notice.read_count}</span></span>
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Acknowledged: <span className="font-medium text-foreground">{notice.ack_count}</span></span>
            {notice.delivery_started_at && (
              <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Sent: {formatDateTime(notice.delivery_started_at)}</span>
            )}
          </div>
        )}

        {/* Compliance table */}
        <div className="mt-2">
          <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
            <BarChart3 className="h-3.5 w-3.5 text-primary" />
            Compliance — {notice.read_count} read · {notice.ack_count} acknowledged
          </h4>
          <div className="overflow-x-auto rounded-lg border border-border/40">
            <table className="w-full text-xs">
              <thead className="bg-primary text-primary-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Name</th>
                  <th className="px-3 py-2 text-left font-semibold">Employee Code</th>
                  <th className="px-3 py-2 text-left font-semibold">Read At</th>
                  <th className="px-3 py-2 text-left font-semibold">Acknowledged At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/30">
                {readsQuery.isLoading ? (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">Loading…</td></tr>
                ) : readsQuery.data?.data.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-4 text-center text-muted-foreground">No reads yet</td></tr>
                ) : (
                  (readsQuery.data?.data ?? []).map((r: any) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2">{r.user_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.employee_code ?? '—'}</td>
                      <td className="px-3 py-2">{formatDateTime(r.read_at)}</td>
                      <td className="px-3 py-2">
                        {r.acknowledged_at
                          ? <span className="text-emerald-600">{formatDateTime(r.acknowledged_at)}</span>
                          : <span className="text-muted-foreground">—</span>
                        }
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Reads pagination */}
          {(readsQuery.data?.pagination?.totalPages ?? 1) > 1 && (
            <div className="flex items-center justify-end gap-2 mt-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={readsPage <= 1}
                onClick={() => setReadsPage((p) => p - 1)}
              >
                Prev
              </Button>
              <span className="text-xs text-muted-foreground">
                {readsPage} / {readsQuery.data?.pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={readsPage >= (readsQuery.data?.pagination.totalPages ?? 1)}
                onClick={() => setReadsPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const STATUS_TABS = ['all', 'draft', 'published', 'archived'] as const;
type StatusTab = (typeof STATUS_TABS)[number];

export function HrmsNoticePage() {
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [typeFilter, setTypeFilter] = useState<string>(ALL);
  const [priorityFilter, setPriorityFilter] = useState<string>(ALL);
  const [q, setQ] = useState('');
  const [dialog, setDialog] = useState<DialogState>(null);

  const params = {
    page,
    limit: 10,
    q: q || undefined,
    status: statusTab !== 'all' ? (statusTab as NoticeStatus) : undefined,
    notice_type: typeFilter !== ALL ? (typeFilter as NoticeType) : undefined,
    priority: priorityFilter !== ALL ? (priorityFilter as NoticePriority) : undefined,
  };

  const listQuery = useQuery({
    queryKey: ['notices', 'admin', params],
    queryFn: () => noticeService.list(params),
  });

  const statsQuery = useQuery({
    queryKey: ['notices', 'admin', 'stats'],
    queryFn: () => noticeService.stats(),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['notices', 'admin'] });
    void qc.invalidateQueries({ queryKey: ['notices', 'my'] });
  };

  const publishMut = useMutation({
    mutationFn: (id: string) => noticeService.publish(id),
    onSuccess: () => { toast.success('Notice published'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const archiveMut = useMutation({
    mutationFn: (id: string) => noticeService.archive(id),
    onSuccess: () => { toast.success('Notice archived'); invalidate(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => noticeService.remove(id),
    onSuccess: () => { toast.success('Notice deleted'); invalidate(); setDialog(null); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<NoticeWithMeta>[] = [
    {
      key: 'number',
      header: '#',
      cell: (row) => <span className="font-mono text-[11px] text-muted-foreground">{row.notice_number}</span>,
      className: 'w-28',
    },
    {
      key: 'title',
      header: 'Title',
      cell: (row) => (
        <button
          type="button"
          onClick={() => setDialog({ type: 'view', notice: row })}
          className="text-left hover:text-primary transition-colors line-clamp-1 font-medium"
        >
          {row.title}
        </button>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      cell: (row) => (
        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 rounded-full border ${TYPE_STYLES[row.notice_type]}`}>
          {row.notice_type}
        </Badge>
      ),
      className: 'w-24',
    },
    {
      key: 'priority',
      header: 'Priority',
      cell: (row) => (
        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 rounded-full border ${PRIORITY_STYLES[row.priority]}`}>
          {row.priority}
        </Badge>
      ),
      className: 'w-24',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <Badge variant="outline" className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[row.status]}`}>
          {STATUS_LABELS[row.status]}
        </Badge>
      ),
      className: 'w-24',
    },
    {
      key: 'target',
      header: 'Target',
      cell: (row) => (
        <span className="text-xs text-muted-foreground capitalize">{row.target_type}</span>
      ),
      className: 'w-20',
    },
    {
      key: 'published',
      header: 'Published',
      cell: (row) => (
        <span className="text-xs text-muted-foreground">{formatDate(row.published_at)}</span>
      ),
      className: 'w-28',
    },
    {
      key: 'delivered',
      header: 'Delivered',
      cell: (row) => (
        <span className="text-xs text-muted-foreground">{row.delivered_count}</span>
      ),
      className: 'w-20',
    },
    {
      key: 'reads',
      header: 'Reads',
      cell: (row) => (
        <span className="text-xs">{row.read_count} / {row.ack_count} ack</span>
      ),
      className: 'w-24',
    },
    {
      key: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            title="View"
            onClick={() => setDialog({ type: 'view', notice: row })}
          >
            <Eye className="h-3.5 w-3.5" />
          </Button>
          {row.status !== 'archived' && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              title="Edit"
              onClick={() => setDialog({ type: 'form', notice: row })}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {row.status === 'draft' && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
              title="Publish"
              disabled={publishMut.isPending}
              onClick={() => publishMut.mutate(row.id)}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
          {row.status === 'published' && (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-amber-600 hover:text-amber-700"
              title="Archive"
              disabled={archiveMut.isPending}
              onClick={() => archiveMut.mutate(row.id)}
            >
              <Archive className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-rose-500 hover:text-rose-600"
            title="Delete"
            onClick={() => setDialog({ type: 'delete', notice: row })}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
      className: 'w-40',
    },
  ];

  const stats = statsQuery.data;

  return (
    <div className="space-y-5">
      {/* Page header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight">Manage Notices</h1>
          <p className="text-sm text-muted-foreground">Create, publish and track company notices</p>
        </div>
        <PermissionGate permission={PERMISSIONS.NOTICE_MANAGE}>
          <Button
            size="sm"
            className="h-9 bg-teal-600 hover:bg-teal-700 text-white gap-1.5"
            onClick={() => setDialog({ type: 'form', notice: null })}
          >
            <Plus className="h-4 w-4" />
            New Notice
          </Button>
        </PermissionGate>
      </div>

      {/* Stat cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Total"
          value={statsQuery.isLoading ? '—' : (stats?.total ?? 0)}
          icon={FileText}
          tone="sky"
        />
        <StatCard
          label="Published"
          value={statsQuery.isLoading ? '—' : (stats?.published ?? 0)}
          icon={Send}
          tone="emerald"
        />
        <StatCard
          label="Drafts"
          value={statsQuery.isLoading ? '—' : (stats?.draft ?? 0)}
          icon={Pencil}
          tone="amber"
        />
        <StatCard
          label="Archived"
          value={statsQuery.isLoading ? '—' : (stats?.archived ?? 0)}
          icon={Archive}
          tone="violet"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Status tabs */}
        <Tabs
          tabs={STATUS_TABS.map((tab) => ({ key: tab, label: tab === 'all' ? 'All' : STATUS_LABELS[tab as NoticeStatus] }))}
          active={statusTab}
          onChange={(k) => { setStatusTab(k as StatusTab); setPage(1); }}
        />

        {/* Type filter */}
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-full sm:w-[140px] text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Types</SelectItem>
            <SelectItem value="general">General</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="policy">Policy</SelectItem>
            <SelectItem value="event">Event</SelectItem>
            <SelectItem value="circular">Circular</SelectItem>
          </SelectContent>
        </Select>

        {/* Priority filter */}
        <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 w-full sm:w-[140px] text-xs">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="normal">Normal</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>

        {/* Search */}
        <div className="sm:ml-auto sm:w-[220px]">
          <SearchInput
            value={q}
            onChange={(v) => { setQ(v); setPage(1); }}
            placeholder="Search notices…"
            className="h-8 text-xs"
          />
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        rows={listQuery.data?.data ?? []}
        rowKey={(r) => r.id}
        loading={listQuery.isLoading}
        error={listQuery.isError}
        onRetry={() => listQuery.refetch()}
        empty={
          <div className="py-10 text-center">
            <Bell className="mx-auto h-8 w-8 text-muted-foreground/40" />
            <p className="mt-2 text-sm text-muted-foreground">No notices found</p>
          </div>
        }
        pagination={
          listQuery.data
            ? {
                page,
                totalPages: listQuery.data.pagination.totalPages,
                total: listQuery.data.pagination.total,
                onPageChange: setPage,
              }
            : undefined
        }
      />

      {/* Dialogs */}
      {dialog?.type === 'form' && (
        <NoticeFormDialog
          notice={dialog.notice}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.type === 'view' && (
        <NoticeViewDialog
          notice={dialog.notice}
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.type === 'delete' && (
        <AlertDialog open onOpenChange={(open) => { if (!open) setDialog(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Notice</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete &ldquo;{dialog.notice.title}&rdquo;? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-rose-600 hover:bg-rose-700 text-white"
                onClick={() => deleteMut.mutate(dialog.notice.id)}
                disabled={deleteMut.isPending}
              >
                {deleteMut.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
